/**
 * Maintained by Sandon.
 */
import Tree, {defaultProps, Modes} from './index'
import {arrayContentSame} from './utils'

// 使用的组件： tree/cascader
const coreMixin = {
  data () {
    const tree = new Tree(this.props, this.lazy, this.multiple, this.mode, this.checkAncestorFn, this.labelDivider, this.simple)
    const root = tree.root
    return {
      tree,
      root,
      keys: Object.assign({}, defaultProps, this.props),
      // 选中数据的完整模型
      selectedNodes: {
        completeNodes: [],
        simpleNodes: [],
        complete: [],
        simple: [],
        unInitSelectedKeys: []
      },
      preSelectedKeys: []
    }
  },
  watch: {
    selectedKeys: function (newVal, oldVal) {
      // 如果 selectedKeys 有改变，则以 selectedKeys 为准重新渲染
      let existSelectedKeys = this.selectedNodes.simpleNodes.map(node => node.key)
      existSelectedKeys = existSelectedKeys.concat(this.selectedNodes.unInitSelectedKeys)
      
      if (!arrayContentSame(existSelectedKeys, this.selectedKeys)) {
        this._initSelected()
      }
    },
    data: function () {
      if (Array.isArray(this.data)) {
        this._initTree()
        this._initSelected()
      }
    }
  },
  created () {
    if (!this.lazy) {
      // 全量数据
      this._initTree()
      this._initSelected()
    } else if (this.selectedKeys.length) {
      // 懒加载 + 有selectedKeys
      // 只修改 unInitSelectedKeys
      this.selectedNodes.unInitSelectedKeys =
        this.multiple ? [...this.selectedKeys] : [this.selectedKeys[0]]
    }
  },
  methods: {
    // 基于 data 属性初始化一棵树
    _initTree () {
      const childrenKey = this.keys['children']
      this.root.data[childrenKey] = this.data // 根节点的 data.children 记录整个数据
      let arr = [this.root]
      for (let i = 0; i !== arr.length; i++) {
        this._loadCb(arr[i], null, (arr[i].data && arr[i].data[childrenKey]) || [])
        arr = arr.concat(arr[i].childNodes)
      }
      this.$emit('tree-initialized', this.root)
    },
    // 对已有树节点初始化预选中（全部Node节点或给定Node集合）
    // 给定Node集合的场景是：懒加载子节点后进行下初始化
    // 传入nodes参数则代表只对此nodes集合做初始化，不传nodes参数则对全部节点做初始化
    _initSelected (nodes) {
      this.multiple ? this._initSelectedMultiple(nodes) : this._initSelectedSingle(nodes)
    },
    // 单选的初始化
    _initSelectedSingle (nodes) {
      const selectedNodes = this.selectedNodes
      
      if (!nodes) {
        // 全局初始化
        if (!this.selectedKeys.length) {
          selectedNodes.unInitSelectedKeys = []
          this._updateSelectedNodesSingle(null, true)
        } else {
          // 全局遍历和寻找
          const targetKey = this.selectedKeys[0]
          
          let selected = null
          let arr = this.root.childNodes
          for (let i = 0; i !== arr.length; i++) {
            if (arr[i].key === targetKey) {
              selected = arr[i]
              break
            }
            arr = arr.concat(arr[i].childNodes)
          }
          
          selectedNodes.unInitSelectedKeys = selected ? [] : [targetKey]
          this._updateSelectedNodesSingle(selected, true)
        }
      } else {
        // 局部初始化
        if (selectedNodes.unInitSelectedKeys.length) {
          // 局部遍历和寻找
          const targetKey = selectedNodes.unInitSelectedKeys[0]
          const selected = nodes.find(_ => _.key === targetKey) || null
          if (selected) {
            selectedNodes.unInitSelectedKeys = []
            this._updateSelectedNodesSingle(selected, true)
          }
        }
      }
    },
    // 多选的初始化
    _initSelectedMultiple (nodes) {
      const selectedNodes = this.selectedNodes
      let targetCheckedKeys
      if (!nodes) {
        // 全局初始化
        targetCheckedKeys = this.selectedKeys
      } else {
        // 局部初始化
        targetCheckedKeys = selectedNodes.unInitSelectedKeys
          .concat(selectedNodes.completeNodes.map(_ => _.key))
      }
      
      const {checkedNodes, leftKeys} = this.tree.setCheckedKeys(targetCheckedKeys, true)
      selectedNodes.unInitSelectedKeys = leftKeys
      
      this._updateSelectedNodesMultiple(checkedNodes, true)
    },
    // 执行load函数
    _callLoad (node, cb) {
      node.loading = true
      this.load(node, this._loadCb.bind(this, node, cb))
    },
    // load 属性中传入的cb，使用data数组为node节点渲染子元素
    _loadCb (parent, cb, dataArr) {
      !dataArr && (dataArr = [])
      if (!Array.isArray(dataArr)) {
        throw new Error('load data should be Array')
      }
      // parent.loaded = true
      // parent.loading = false
      // parent.isLeaf = dataArr.length === 0
      // dataArr.forEach(data => parent.appendDataChild(data))
      parent.initChildren(dataArr)
      
      // 懒加载时
      // if (this.lazy) {
      //   if (this.selectedNodes.unInitSelectedKeys.length) {
      //     // 还有unInitSelectedKeys未初始化，则执行初始化
      //     this._initSelected(parent.childNodes)
      //   } else if (this.multiple && parent.checked && parent.childNodes.length) {
      //     // 此节点选中，在'twoWay'模式下子节点也会被选中, 'down'模式下也有可能被选中
      //     this._updateSelectedNodesMultiple()
      //   }
      // }
      this._childrenChanged(parent)
      
      cb && cb()
    },
    // 因展开加载，或通过api新增children导致 children 变化时需要做的处理
    _childrenChanged (parent) {
      // 懒加载时
      if (this.lazy) {
        if (this.selectedNodes.unInitSelectedKeys.length) {
          // 还有unInitSelectedKeys未初始化，则执行初始化
          this._initSelected(parent.childNodes)
        } else if (this.multiple && parent.checked && parent.childNodes.length) {
          // 此节点选中，在'twoWay'模式下子节点也会被选中, 'down'模式下也有可能被选中
          this._updateSelectedNodesMultiple()
        }
      }
    },
    // node的checked改变，从而改变对应的祖先和子孙节点
    _updateCheckedMultiple (node, targetChecked) {
      node.updateCheckedMultiple(targetChecked)
      
      // 更新选中节点数组
      this._updateSelectedNodesMultiple()
      
      // check-change 事件
      this.$emit('check-change', node)
    },
    // 多选 - 更新选中节点
    _updateSelectedNodesMultiple (completeSelectedNodes, silent = false) {
      if (!completeSelectedNodes) {
        // 广度优先遍历树，获取选中的节点,注意不要选择根节点
        completeSelectedNodes = [] // 所有选中的
        let arr = [this.root]
        for (let i = 0; i !== arr.length; i++) {
          const node = arr[i]
          node.parent && node.checked && completeSelectedNodes.push(node)
          arr = arr.concat(node.childNodes)
        }
      }
      
      const simpleSelectedNodes = this.tree.refineNodes(completeSelectedNodes)
      
      // 最终的数据
      this.selectedNodes.completeNodes = completeSelectedNodes
      this.selectedNodes.simpleNodes = simpleSelectedNodes
      
      this._change(silent)
    },
    // 单选 - 更新选中节点
    _updateSelectedNodesSingle (node, silent = false, cb = null, clearUnInit = false) {
      if (node && !node.parent) return // 根节点不让选中
      
      const selectedNodes = this.selectedNodes
      
      selectedNodes.simpleNodes.length && selectedNodes.simpleNodes[0].updateCheckedSingle(false)
      node && node.updateCheckedSingle(true)
      
      // 更新selectedNodes
      const nodes = node ? [node] : []
      // let completeNodes = nodes
      // if (this.mode === Modes.TWO_WAY) {
      //   for (let i = 0; i !== completeNodes.length; i++) {
      //     completeNodes = completeNodes.concat(completeNodes[i].childNodes)
      //   }
      // }
      // this.selectedNodes.completeNodes = completeNodes
      selectedNodes.completeNodes = nodes
      selectedNodes.simpleNodes = nodes
      clearUnInit && (selectedNodes.unInitSelectedKeys = [])
      this._change(silent)
      
      // 关闭弹层
      // !silent && this._close()
      
      cb && cb()
    },
    // 变化而导致：修改展示，触发事件 - core-mixin 依赖
    _change (silent = false) {
      const selectedNodes = this.selectedNodes
    
      // 完善selectedNodes.complete和selectedNodes.simple
      selectedNodes.complete = selectedNodes.completeNodes.map(node => node.data)
      selectedNodes.simple = selectedNodes.simpleNodes.map(node => node.data)
    
      // 构建最新的 selectedKeys
      let selectedKeys = selectedNodes.simpleNodes.map(node => node.key)
      selectedKeys = selectedKeys.concat(selectedNodes.unInitSelectedKeys)
    
      // 发送事件
      !silent && this.$emit('update:selectedKeys', selectedKeys)
      this.$emit(silent ? 'silent-change' : 'change', selectedNodes, [...selectedKeys], this.preSelectedKeys)
  
      // 记录 preSelectedKeys
      this.preSelectedKeys = [...selectedKeys]
    },
    // 触发 "展开"
    async _expand (node) {
      // 加载未加载的子级数据
      if (this.lazy && !node.loaded && !node.loading) {
        await new Promise((resolve, reject) => {
          const cbAfterLoad = () => {
            // 单选的叶子节点在点击时，load完在确定其为叶子节点后，选中此叶子节点
            // if (!this.multiple && node.loaded && node.isLeaf) {
            //   this._updateSelectedNodesSingle(node, true)
            // }
    
            this._doExpand(node)
            resolve()
          }
          this._callLoad(node, cbAfterLoad)
        })
        
        // const cbAfterLoad = () => {
        //   // 单选的叶子节点在点击时，load完在确定其为叶子节点后，选中此叶子节点
        //   // if (!this.multiple && node.loaded && node.isLeaf) {
        //   //   this._updateSelectedNodesSingle(node, true)
        //   // }
        //  
        //   this._doExpand(node)
        // }
        // this._callLoad(node, cbAfterLoad)
      } else {
        this._doExpand(node)
      }
    },
    // 自动展开
    _autoExpand (parent, expandPathIndex, eachCb, finalCb) {
      const key = this.expandPath[expandPathIndex]
      const node = parent.childNodes.find(node => node.key === key)
      
      if (!node) {
        console.warn(`自动展开路径中没有节点匹配${key}`)
        finalCb && finalCb()
        return
      }
      
      const newIndex = expandPathIndex + 1
      if (newIndex < this.expandPath.length) {
        this.load(node, this._loadCb.bind(this, node, () => {
          eachCb && eachCb(node)
          this._autoExpand(node, newIndex, eachCb, finalCb)
        }))
      } else {
        this.load(node, this._loadCb.bind(this, node, () => {
          eachCb && eachCb(node)
          finalCb && finalCb(node)
        }))
      }
    },
    // 复选框被点击 - 多选
    _checkboxClick (node) {
      if (node.disabled) return
    
      // 注意：后续的逻辑，由于使用的el-checkbox, node.checked 已经被改变
    
      // down 模式时，取消勾选时，连带取消未初始化的key, 此key是此节点的子孙；
      // 因为unInitSelectedKeys 是在tree中管理，所以这段逻辑在此处理
      if (this.mode === Modes.DOWN && this.lazy && this.multiple && this.checkAncestorFn && !node.checked) {
        this.selectedNodes.unInitSelectedKeys = this.selectedNodes.unInitSelectedKeys.filter((key) => {
          return !this.checkAncestorFn(node, key)
        })
      }
    
      // 收集选中节点信息
      this._updateCheckedMultiple(node, node.checked)
    
      // checkbox-click 事件
      this.$emit('checkbox-click', node)
    },
    // 对外接口 - down/separate模式下，一个节点代表整颗子树选中时的初始化
    // 这里对场景做了假设和要求：只会在初始化时（即在用户做任何操作之前）进行设置，且最多只会调用1次
    setKeysSubTreeChecked (keysSubTreeChecked) {
      if (!(this.lazy && this.multiple && (this.mode === Modes.DOWN || this.mode === Modes.SEPARATE))) return
      this.tree.setKeysSubTreeChecked(keysSubTreeChecked)
      this._initSelected()
    },
    // 对外接口 - 遗留的接口，todo 业务代码迁移后将删除
    initDownMode (keysSubTreeChecked) {
      this.setKeysSubTreeChecked(keysSubTreeChecked)
    },
    // 对外接口 - 返回整棵子树都被选中的根节点
    getKeysSubTreeChecked () {
      return this.tree.getKeysSubTreeChecked() // todo
    }
  }
}

export default coreMixin